import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe, NgFor, NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { VendorService } from '../../vendor.service';
import { ImpersonationService } from '../../../auth/impersonation.service';
import { ItemCreateService } from './item-create.service';
import {
  CATEGORY_DIFFERS_FROM_MASTER,
  ICloneSource,
  ICreateItemAttribute,
  ICreateItemDiscountQty,
  ICreateItemRequest,
  IItemProblemDetails,
  IItemResolution,
  ILookupAttribute,
  ILookupCategory,
  ILookupExwPoint,
  ILookupManufacturer,
  ItemResponse,
  IWriteWarning
} from './item-create.models';
import { ItemTextModalComponent } from './item-text-modal/item-text-modal.component';
import { IVendorCompany } from '../../../shared/interfaces/model';

/**
 * Create Item modal (HUBX-8385). Visually mirrors the item-detailed edit popup
 * but is a standalone component with its own form: none of the edit machinery
 * (updatedItem diffing, undo, pending offers) applies to Create.
 *
 * Role behavior (design doc §8.3 / ADR-0009):
 * - Status dropdown maps to the CreateApproved flag only (Active=true, Pending=false).
 *   SELLER: locked to Pending. ADMIN/VM: selectable, with a consequence hint.
 * - XID, Price, DQ #1-3: ADMIN/VM only. MOQ/MXQ: all roles. Cost: all roles.
 * - Restrictions / Special Notes: state chips opening a shared text modal;
 *   empty text means the member is omitted from the request.
 *
 * Phase 3 (this change): validators mirroring the edge/handler contract
 * (CreateItemRequest.cs, design doc §4.1). Requiredness follows the contract as
 * shipped, NOT the coarse layout asterisks:
 * - required: VendorItemCode, Mpn, Description, ManufacturerId, CategoryId,
 *   Condition, Packaging, Warranty, ExwId, Availability, LeadTimeDays, Cost.
 * - Cost (UnitPurchasePrice) is required for EVERY role (PricingPolicy);
 *   UnitPrice is required for ADMIN/VM only and is disabled + never sent for
 *   SELLER (a Seller supplying it is UNIT_PRICE_NOT_ALLOWED, 403).
 * - MOQ is OPTIONAL (§4.1, 2026-07-20: absent -> server default 1); ≥1 if present.
 * - HubxItemCode (XID) is optional, ADMIN/VM-only (disabled for SELLER -> A-R10 default).
 * - The three attributes are the client mirror of the handler's
 *   REQUIRED_ATTRIBUTES_MISSING; the edge floor is only "≥1 attribute".
 * Cross-field mirrors (server is the truth): MOQ≤MXQ (MOQ_EXCEEDS_MXQ),
 * UnitPrice≥Cost (UNIT_PRICE_BELOW_COST), and per DQ tier: complete pair,
 * FromQty≥2 (DISCOUNT_QTY_BELOW_MINIMUM), Price<UnitPrice
 * (DISCOUNT_PRICE_EXCEEDS_UNIT_PRICE). The 3P cost-margin rule
 * (DISCOUNT_PRICE_BELOW_COST_MARGIN) stays server-only — the client cannot know
 * BP group membership reliably.
 *
 * Error presentation follows the item-detailed edit popup: a red ring on each
 * invalid control marks WHERE, and a single prioritized message banner at the
 * bottom of the footer says WHAT.
 *
 * Phase 4: submit() builds ICreateItemRequest (empty optionals omitted, never
 * null; attributes {attributeId, value:code}; createApproved only for ADMIN/VM;
 * SELLER-disabled controls drop out of fg.value), POSTs via ItemCreateService,
 * emits `created` with the new id and submitted code on 201 (parent closes,
 * reloads, and confirms), and routes
 * ProblemDetails by its stable `code` to the mapped control + banner.
 */
@Component({
  selector: 'app-item-create-modal',
  templateUrl: './item-create-modal.component.html',
  styleUrls: ['./item-create-modal.component.scss'],
  standalone: true,
  imports: [NgIf, NgFor, ReactiveFormsModule, FormsModule, TooltipModule, ItemTextModalComponent, DatePipe]
})
export class ItemCreateModalComponent implements OnInit {
  @Output() closed = new EventEmitter<void>();
  /** `categoryNotice` is present only when the write-ack carried the category
   *  divergence advisory (HUBX-8429) — already resolved to display text here,
   *  because the category names live in this component's lookup tree. */
  @Output() created = new EventEmitter<{ id: string; vendorItemCode: string; categoryNotice?: string }>();

  /** Clone (HUBX-8368): when present, the modal is seeded from an existing item.
   *  Set once by the parent before creation (the modal is instantiated behind an
   *  *ngIf), so it is available in ngOnInit. Absent => a blank Create. The seed is
   *  applied AFTER lookups load — category resolution walks the tree and the three
   *  attributes match by attributeId, both of which need the lookups in hand. */
  @Input() seed?: ItemResponse;

  /** Clone (HUBX-8422): the item the seed came FROM, for the header strip. Its
   *  presence is what makes this a clone as far as the template is concerned —
   *  the parent sets it and `seed` together. Display only: nothing here is read
   *  by the form or the request. */
  @Input() source?: ICloneSource;

  fg: FormGroup;
  loader = false;
  lookupsError = false;
  submitted = false;

  /** Server-side failure message (routed from ProblemDetails.code), shown in the
   *  banner with top priority; cleared on the next edit or submit. */
  serverError: string | null = null;

  manufacturers: ILookupManufacturer[] = [];
  categories: ILookupCategory[] = [];
  subCategories: ILookupCategory[] = [];
  condition: ILookupAttribute;
  packaging: ILookupAttribute;
  warranty: ILookupAttribute;
  exwPoints: ILookupExwPoint[] = [];

  /** Target company (ADR-0010, HUBX-8421). The companies this operator may write
   *  for, from the admin API's impersonation surface — the same source as the
   *  portal's company switcher, read-only. Empty for a SELLER (never fetched) and
   *  empty if the fetch fails, and an empty list hides the control: a failure to
   *  load companies must not block creating for the company you are already in. */
  companies: IVendorCompany[] = [];

  /** The company this session is currently acting as, as the client sees it. Used
   *  only to preselect the target selector's default — NOT to decide what to send.
   *  Recognising a selection as "no retarget" is the server's job against its
   *  authoritative context, which this client-side value (from sessionStorage) can
   *  lag. */
  private ambientVendorId: string | null = null;

  /** Which text field the shared text modal is editing; null = closed. */
  textModalField: 'restrictions' | 'specialNotes' | null = null;

  /** Last answer from GET /items/resolutions for the MPN currently in the form
   *  (HUBX-8430/8431); null before the first answer, on a blank MPN, or when the
   *  call failed. Only the MPN refreshes it — a category change re-reads THIS,
   *  never the network (see categoryNudge). */
  private mpnResolution: IItemResolution | null = null;

  readonly sellerStatusHint = 'New items require HUBX review before going live';

  private destroyRef = inject(DestroyRef);

  constructor(
    public vendorService: VendorService,
    private itemCreateService: ItemCreateService,
    private fb: FormBuilder
  ) { }

  get isPricingRole(): boolean {
    return this.vendorService.isAdmin || this.vendorService.isVendorManager;
  }

  /** The target selector renders for operator roles as soon as the modal opens — it
   *  is NOT gated on the company list. The closed control only needs a label, and the
   *  label of the company we are already acting as is in the session; the list is
   *  needed solely to open the panel. Waiting for the fetch would leave a hole in the
   *  band for as long as the admin API takes. */
  get showTargetSelector(): boolean {
    return this.isPricingRole;
  }

  /** The provenance band renders when it has something to hold: a clone source on
   *  the left (HUBX-8422), a target selector on the right (ADR-0010), or both. On a
   *  plain Add Item for a SELLER it collapses entirely. */
  get showBand(): boolean {
    return !!this.source || this.showTargetSelector;
  }

  get dqControls(): UntypedFormArray {
    return this.fg.get('discountQtys') as UntypedFormArray;
  }

  get statusHint(): string {
    return this.fg.controls.status.value === 'Active'
      ? 'Goes live immediately'
      : 'Held for HUBX review';
  }

  get textModalTitle(): string {
    return this.textModalField === 'restrictions' ? 'Restrictions' : 'Special Notes';
  }

  get textModalValue(): string {
    return this.textModalField ? this.fg.controls[this.textModalField].value : '';
  }

  ngOnInit(): void {
    this.buildForm();
    this.loadLookups();
    if (this.isPricingRole) {
      this.loadCompanies();
    }

    // UnitPrice feeds the per-tier Price<UnitPrice check; the DQ group validators
    // read it lazily, so re-run them whenever UnitPrice changes. (No-op for SELLER:
    // UnitPrice is disabled, so it emits nothing.)
    this.fg.controls.unitPrice.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dqControls.controls
        .forEach(c => c.updateValueAndValidity({ onlySelf: true })));

    // A stale server error must not outlive the edit that could fix it.
    this.fg.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.serverError) {
          this.serverError = null;
        }
      });

    // Pre-submit category nudge (HUBX-8431): ask the server what item this MPN
    // already resolves to, and under which category. ONLY the MPN triggers a
    // call — a category change re-reads the stored answer (categoryNudge), never
    // the network. switchMap drops the in-flight answer for a superseded MPN, so
    // a fast typist can never be shown a nudge for what they used to have typed.
    // The Clone seed patches `mpn`, which emits here on purpose: a clone whose
    // category diverges from the shared item's should say so before submit, not
    // only after save (HUBX-8429).
    this.fg.controls.mpn.valueChanges
      .pipe(
        debounceTime(400),
        map((value: string) => (value ?? '').trim()),
        distinctUntilChanged(),
        // A blank MPN resolves to nothing and is a 400 at the endpoint, so it is
        // never sent. catchError sits INSIDE the inner observable: outside, one
        // failed lookup would complete the outer stream and kill the nudge for
        // the rest of the modal's life.
        switchMap(mpn => mpn
          ? this.itemCreateService.resolveByMpn(mpn).pipe(catchError(() => of(null)))
          : of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(resolution => this.mpnResolution = resolution);
  }

  buildForm(): void {
    const pricing = this.isPricingRole;

    this.fg = this.fb.group({
      status: { value: 'Pending', disabled: !pricing },
      vendorItemCode: ['', [Validators.required, Validators.maxLength(50), ItemCreateModalComponent.notBlank]],
      // XID: ADMIN/VM only (§2 amended 2026-07-21). Optional; blank/absent -> A-R10
      // default (= VendorItemCode), so no required and no blank check.
      hubxItemCode: [{ value: '', disabled: !pricing }, [Validators.maxLength(50)]],
      mpn: ['', [Validators.required, Validators.maxLength(50), ItemCreateModalComponent.notBlank]],
      description: ['', [Validators.required, Validators.maxLength(250), ItemCreateModalComponent.notBlank]],
      manufacturerId: ['', Validators.required],
      categoryMainId: '',
      categoryId: ['', Validators.required],
      condition: ['', Validators.required],
      packaging: ['', Validators.required],
      warranty: ['', Validators.required],
      exwId: ['', Validators.required],
      availability: [null, [Validators.required, Validators.min(0)]],
      leadTimeDays: [null, [Validators.required, Validators.min(0)]],
      // MOQ optional (server default 1); MXQ optional (0/absent ≡ "no maximum").
      moq: [null, Validators.min(1)],
      mxq: [null, Validators.min(0)],
      // Cost (UnitPurchasePrice): required for every role.
      cost: [null, [Validators.required, Validators.min(0.01)]],
      // Sale price: ADMIN/VM only; disabled (and thus omitted from fg.value) for SELLER.
      unitPrice: [
        { value: null, disabled: !pricing },
        pricing ? [Validators.required, Validators.min(0.01)] : []
      ],
      restrictions: '',
      specialNotes: '',
      // Target company (ADR-0010). Disabled for SELLER, which is what keeps the
      // member out of fg.value entirely — the same mechanism as UnitPrice/XID, so a
      // Seller can never send it even if the control were somehow rendered.
      // Never `required`: absent means "the company I'm already in", which is the
      // default and the overwhelmingly common case. Empty string, not null, to match
      // every other select in this form and its `<option value="">` placeholder.
      targetVendorId: [{ value: '', disabled: !pricing }],
      discountQtys: this.fb.array([0, 1, 2].map(() => this.fb.group(
        {
          fromQty: { value: null, disabled: !pricing },
          price: { value: null, disabled: !pricing }
        },
        { validators: ItemCreateModalComponent.dqTierValidator }
      )))
    }, {
      validators: [ItemCreateModalComponent.moqMxqValidator, ItemCreateModalComponent.unitPriceCostValidator]
    });
  }

  /** Required string that is present but only whitespace (mirrors the edge Validate). */
  private static notBlank: ValidatorFn = (c: AbstractControl): ValidationErrors | null =>
    typeof c.value === 'string' && c.value.length > 0 && c.value.trim().length === 0
      ? { blank: true }
      : null;

  /** MOQ ≤ MXQ when both present and MXQ is a real maximum (>0). -> MOQ_EXCEEDS_MXQ. */
  private static moqMxqValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    const moq = group.get('moq')?.value;
    const mxq = group.get('mxq')?.value;
    return moq != null && mxq != null && mxq > 0 && moq > mxq ? { moqExceedsMxq: true } : null;
  };

  /** UnitPrice ≥ Cost when both present. -> UNIT_PRICE_BELOW_COST. */
  private static unitPriceCostValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    const cost = group.get('cost')?.value;
    const unitPrice = group.get('unitPrice')?.value;
    return cost != null && unitPrice != null && unitPrice < cost ? { unitPriceBelowCost: true } : null;
  };

  /** A discount tier is either empty (ignored) or a complete pair with FromQty ≥ 2
   *  and Price < UnitPrice. The 3P cost-margin rule stays server-only. */
  private static dqTierValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    const fromQty = group.get('fromQty')?.value;
    const price = group.get('price')?.value;
    const hasFrom = fromQty !== null && fromQty !== '' && fromQty !== undefined;
    const hasPrice = price !== null && price !== '' && price !== undefined;
    if (!hasFrom && !hasPrice) {
      return null;
    }

    const errors: ValidationErrors = {};
    if (!hasFrom || !hasPrice) {
      errors['incompletePair'] = true;
    }
    if (hasFrom && fromQty < 2) {
      errors['fromQtyBelowMin'] = true;
    }
    const unitPrice = group.parent?.parent?.get('unitPrice')?.value;
    if (hasPrice && unitPrice != null && price >= unitPrice) {
      errors['priceExceedsUnit'] = true;
    }
    return Object.keys(errors).length ? errors : null;
  };

  /** Control-level error, shown only after the user has engaged the field. */
  showFieldError(name: string): boolean {
    const c = this.fg.get(name);
    return !!c && c.invalid && (c.touched || c.dirty);
  }

  /** Form-group cross-field error, shown once any trigger control is engaged. */
  showGroupError(key: string, ...triggers: string[]): boolean {
    if (!this.fg.errors?.[key]) {
      return false;
    }
    return triggers.some(t => {
      const c = this.fg.get(t);
      return !!c && (c.touched || c.dirty);
    });
  }

  /** Per-tier discount error, shown once that tier is engaged. */
  dqError(index: number, key: string): boolean {
    const g = this.dqControls.at(index);
    return !!g && g.hasError(key) && (g.touched || g.dirty);
  }

  /** Whether a DQ sub-field carries a value, driving the '<' / '$' marker that tells
   *  the two stacked unlabeled inputs apart (parity with the inline edit row, where
   *  the mask prefix appears the moment a value is typed). */
  dqHasValue(group: AbstractControl, key: 'fromQty' | 'price'): boolean {
    const value = group.get(key)?.value;
    return value !== null && value !== undefined && value !== '';
  }

  /** Single consolidated message for the footer banner, mirroring the edit form
   *  (item.service.validateForm): the specific cross-field / discount messages
   *  come first, and a generic pointer to the highlighted fields covers the
   *  remaining required / format / positivity failures. The per-field red rings
   *  (showFieldError) show WHERE; this line says WHAT. */
  get bannerError(): string | null {
    if (this.serverError) {
      return this.serverError;
    }
    if (this.showGroupError('unitPriceBelowCost', 'unitPrice', 'cost')) {
      return 'Price cannot be less than Cost.';
    }
    if (this.showGroupError('moqExceedsMxq', 'moq', 'mxq')) {
      return 'MOQ cannot be greater than MXQ.';
    }
    for (let i = 0; i < this.dqControls.length; i++) {
      if (this.dqError(i, 'incompletePair')) {
        return `DQ #${i + 1}: 'From Qty' and 'Price' are both required to set up a discount.`;
      }
      if (this.dqError(i, 'fromQtyBelowMin')) {
        return `DQ #${i + 1}: 'From Qty' needs to be greater than 1.`;
      }
      if (this.dqError(i, 'priceExceedsUnit')) {
        return `DQ #${i + 1}: discount price cannot exceed or equal the price.`;
      }
    }
    // Generic "complete the fields" only after a submit attempt — never on mere
    // field touch, or it would fire as soon as the user tabs off the first field.
    if (this.fg.invalid && this.submitted) {
      return 'Please complete the highlighted fields.';
    }
    return null;
  }

  /** Pre-submit category nudge (HUBX-8431): the MPN already matches an existing
   *  item filed under a DIFFERENT category than the one selected here. Advisory
   *  only — category is a per-vendor facet (ADR-0007), so diverging is allowed and
   *  nothing blocks submit; the authoritative check is the write-ack warning after
   *  save (HUBX-8429), because a concurrent create can change the resolution
   *  between here and submit.
   *
   *  Silent in all four other cases: no answer yet / the lookup failed, the MPN
   *  resolves to nothing (a new item), the existing item carries no category
   *  (legacy — nothing to compare), or the categories match. Silent too when the
   *  id is not in the lookup tree: the message names the category, so without a
   *  name there is nothing to say. */
  get categoryNudge(): string | null {
    const resolution = this.mpnResolution;
    if (!resolution?.resolved) {
      return null;
    }
    const selected = this.fg.controls.categoryId.value;
    if (!selected || selected === resolution.categoryId) {
      return null;
    }
    return this.listedUnder(resolution.categoryId);
  }

  /** The shared sentence for both category advisories — the pre-submit nudge
   *  (HUBX-8431) and the post-save notice (HUBX-8429). One wording, one place: the
   *  two paths differ only in where the category id comes from (the resolution
   *  preview vs the write-ack warning), never in what they say.
   *
   *  Null when there is no id, or when the id is absent from the lookup tree — the
   *  sentence names the category, so with no name there is nothing to say. */
  private listedUnder(categoryId?: string | null): string | null {
    if (!categoryId) {
      return null;
    }
    const name = this.categoryPath(categoryId);
    return name ? `This part number is usually listed under ${name}.` : null;
  }

  /** Post-save category notice (HUBX-8429): the write-ack's
   *  CATEGORY_DIFFERS_FROM_MASTER advisory, resolved to the same sentence the
   *  pre-submit nudge shows. Built HERE rather than in the parent because the
   *  category name lives in this component's lookup tree; the parent only displays
   *  what it is handed. Unknown codes are ignored — the channel is generic and
   *  will grow codes this build has never heard of. */
  private categoryNoticeFrom(warnings: IWriteWarning[]): string | null {
    const warning = warnings.find(w => w.code === CATEGORY_DIFFERS_FROM_MASTER);
    return this.listedUnder(warning?.data?.['categoryId']);
  }

  /** Display path for a category id, walking the same one-level tree the selects
   *  use: 'Main / Sub' for a child, the plain name for a top-level node, null when
   *  the id is not in the tree. */
  private categoryPath(categoryId: string): string | null {
    for (const top of this.categories) {
      if (top.categoryId === categoryId) {
        return top.name;
      }
      const child = top.children?.find(c => c.categoryId === categoryId);
      if (child) {
        return `${top.name} / ${child.name}`;
      }
    }
    return null;
  }

  loadLookups(): void {
    this.loader = true;
    this.lookupsError = false;
    forkJoin([
      this.itemCreateService.getManufacturers(),
      this.itemCreateService.getCategories(),
      this.itemCreateService.getAttributes(),
      this.itemCreateService.getExwPoints()
    ]).subscribe({
      next: ([manufacturers, categories, attributes, exwPoints]) => {
        this.manufacturers = manufacturers;
        this.categories = categories;
        this.condition = attributes.find(a => a.label === 'Condition');
        this.packaging = attributes.find(a => a.label === 'Packaging');
        this.warranty = attributes.find(a => a.label === 'Warranty');
        this.exwPoints = exwPoints;
        if (this.seed) {
          this.applySeed(this.seed);
        }
        this.applyDefaultExw();
        this.loader = false;
      },
      error: () => {
        this.loader = false;
        this.lookupsError = true;
      }
    });
  }

  /** The ExW point a blank Create defaults to (HUBX-8447), matched by description. */
  private static readonly defaultExwDescription = 'Miami';

  /** Default ExW on a blank Create (HUBX-8447): preselect Miami so the vendor does not
   *  have to state the overwhelmingly common origin on every item.
   *
   *  Resolved by matching the loaded lookup's DESCRIPTION, never by a hardcoded id:
   *  dbo.ExwPoints.Id is an int assigned per environment, so an id that means Miami in
   *  dev means something else in production. Full equality, never a substring — 'Miami'
   *  would also match 'Miami Beach' or 'North Miami', and the list is ordered by
   *  description, so a substring match would silently default to whichever of those
   *  sorts first.
   *
   *  Compared trimmed and case-insensitively because dbo.ExwPoints.Description is
   *  unconstrained nvarchar; a stray space in one environment's data would otherwise
   *  make the default silently not apply, indistinguishable from Miami being absent.
   *  The `?? ''` is load-bearing rather than decorative: the column is NULLABLE and
   *  ExwPointReader does not filter nulls, so a bare .trim() on a null description
   *  would throw HERE, inside the forkJoin next handler — leaving `loader` true and
   *  `lookupsError` false, i.e. the modal stuck on the spinner with nothing to say.
   *
   *  Runs AFTER applySeed and only while the control is still empty. That guard is what
   *  keeps Clone intact: a seeded ExW is the source item's real answer, and the default
   *  must never overwrite it. Edit is a different component (item-detailed) with its own
   *  form, so no stored value is reachable from here.
   *
   *  No Miami in the lookup — deactivated per ADR-0012 / HUBX-8446, or an environment
   *  without one — leaves the control empty and required, and the vendor picks exactly
   *  as before this change. Doing nothing IS the contingency: no error, no banner, no
   *  crash.
   *
   *  Set as a string: the select binds its option values with [value] (stringified), the
   *  same coercion applySeed makes with String(s.exwId); buildRequest converts back with
   *  Number(). setValue leaves the control untouched/pristine, so the preselection shows
   *  no red ring.
   */
  private applyDefaultExw(): void {
    if (this.fg.controls.exwId.value) {
      return;
    }
    const target = ItemCreateModalComponent.defaultExwDescription.toLowerCase();
    const miami = this.exwPoints.find(e => (e.description ?? '').trim().toLowerCase() === target);
    if (miami) {
      this.fg.controls.exwId.setValue(String(miami.id));
    }
  }

  /** Target company list (ADR-0010). Deliberately NOT folded into the lookups
   *  forkJoin: a failure there sets `lookupsError` and blocks submit, but failing to
   *  load the company list must not stop an operator creating for the company it is
   *  already in — on error the list stays empty, the control does not render, and the
   *  create proceeds against the ambient vendor exactly as before this feature.
   *
   *  Fetched per modal open rather than cached. The portfolio is authorization-shaped
   *  data: §7 holds that nothing on the target-authorization path is cached, and a
   *  stale list would offer a company the server will refuse. */
  private loadCompanies(): void {
    this.vendorService.getAllCustomers()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          // Alphabetical by company name so the picker is scannable; the server
          // returns portfolio order, which is arbitrary to the operator. Copy
          // before sorting rather than mutating the service's array, and keep it
          // case-insensitive/locale-aware so "Ábaco" and "abaco" sit together.
          this.companies = [...(result?.customers ?? [])].sort((a, b) =>
            (a.companyName ?? '').localeCompare(b.companyName ?? '', undefined, { sensitivity: 'base' }));
          this.ambientVendorId = this.resolveAmbientCompany(this.companies)?.vendorId ?? null;
          // Preselect the company already being acted as, so the selector shows the
          // operator's current context by default. The value is sent as-is and the
          // server collapses it to a no-op when it equals the authoritative context
          // (buildRequest), so an untouched selector creates under the current company
          // without the client deciding that on its own.
          if (this.ambientVendorId) {
            this.fg.controls.targetVendorId.setValue(this.ambientVendorId);
          }
        },
        error: () => {
          this.companies = [];
        }
      });
  }

  /** Company picker UI state. Both are presentation only — the chosen company lives
   *  in the `targetVendorId` form control, never here, so there is one source of
   *  truth for what gets sent. */
  companyPickerOpen = false;
  companyFilter = '';

  /** Label for the closed control. Two sources, in order: once the list is in, the
   *  name of whatever `targetVendorId` holds — always consistent with what will be
   *  sent. Before that, the company name the session already knows, so the control
   *  shows the right answer from the first frame instead of a placeholder that
   *  flickers into the real value a moment later. */
  get selectedCompanyName(): string {
    const id = this.fg?.controls.targetVendorId.value;
    const fromList = this.companies.find(c => c.vendorId === id)?.companyName;
    return fromList ?? sessionStorage.getItem('company_name') ?? '';
  }

  /** Case-insensitive contains over the list already in memory. The whole portfolio
   *  arrives in one payload, so filtering costs nothing and fetches nothing. */
  get filteredCompanies(): IVendorCompany[] {
    const term = this.companyFilter.trim().toLowerCase();
    return term
      ? this.companies.filter(c => (c.companyName ?? '').toLowerCase().includes(term))
      : this.companies;
  }

  toggleCompanyPicker(): void {
    // Nothing to show until the list lands; the button is disabled meanwhile, and
    // this guard keeps that true even if something else calls in.
    if (!this.companies.length) {
      return;
    }
    this.companyPickerOpen = !this.companyPickerOpen;
    // Clear on every open/close: a term left over from last time would hide most of
    // the list the next time the panel appears, with no visible reason why.
    this.companyFilter = '';
  }

  selectCompany(company: IVendorCompany): void {
    this.fg.controls.targetVendorId.setValue(company.vendorId);
    this.companyPickerOpen = false;
    this.companyFilter = '';
  }

  /** Which company this session is acting as. Settled once when the session was minted
   *  (ImpersonationService) and recorded there, so the switcher and this selector can
   *  never name different companies — both just read it.
   *
   *  In the vendor portal there is always an impersonated company (true as of
   *  2026-07-28, but not enforced anywhere here), so in practice the code is present
   *  and found. When it is present but NOT in the list, this resolves to nothing,
   *  leaving the selector unseeded (blank). buildRequest then sends no target and the
   *  server creates under its authoritative context — the safe outcome. Substituting
   *  some other company would preselect one the operator may not actually be acting
   *  as, so it is deliberately avoided. */
  private resolveAmbientCompany(companies: IVendorCompany[]): IVendorCompany | undefined {
    return companies.find(c => c.bpCode === ImpersonationService.selectedCompanyCode);
  }

  /** Clone source dates, as absolute instants (HUBX-8422).
   *
   *  The API sends these as UTC with NO offset marker — '2026-07-06T05:42:00', the
   *  shape generic-countdown documents in its own sample. ECMAScript parses a
   *  bare date-time as LOCAL, so handing the raw string to DatePipe renders the UTC
   *  clock as though it were the user's own: off by the whole offset, and silently,
   *  because the result is a plausible time. Marking the string as UTC lets the
   *  runtime convert properly, which is the same correction generic-countdown and
   *  offer.service make with `moment.utc(...).tz(...)`. */
  get launchedOnLocal(): Date | null {
    return ItemCreateModalComponent.asInstant(this.source?.launchedOn);
  }

  get updatedOnLocal(): Date | null {
    return ItemCreateModalComponent.asInstant(this.source?.updatedOn);
  }

  private static asInstant(value?: string | Date | null): Date | null {
    if (!value) {
      return null;
    }
    // A Date is already an absolute instant, and a string that states its own
    // offset needs no help — only the bare form is ambiguous, so only it is fixed.
    if (value instanceof Date) {
      return value;
    }
    const statesOffset = /(?:[Zz]|[+-]\d{2}:?\d{2})$/.test(value);
    return new Date(statesOffset ? value : `${value}Z`);
  }

  /** Clone seeding (§4.3): fill the form from an existing item so the new item
   *  starts as a copy. What is deliberately NOT copied stays at its build-form
   *  default, which IS the §4.3 reset: VendorItemCode and Availability blank
   *  (both required — the user supplies new ones), HubxItemCode blank (re-defaults
   *  server-side), Status = Pending, and the commercial terms — Cost, Price and
   *  every DQ tier — left empty (2026-07-28, below). Tags and any attributes beyond
   *  the three the modal models are out of scope — Clone reuses the Create form
   *  as-is (no extra controls, no passthrough). Runs after lookups load. setValue
   *  here does not mark controls touched/dirty, so nothing shows a red ring until
   *  the user engages a field. */
  private applySeed(s: ItemResponse): void {
    // Copied: what DESCRIBES the product. Money is not copied — see below.
    this.fg.patchValue({
      mpn: s.mpn ?? '',
      description: s.description ?? '',
      manufacturerId: s.manufacturerId ?? '',
      leadTimeDays: s.leadTimeDays ?? null,
      moq: s.minimumOrderQty,
      mxq: s.maximumOrderQty ?? null,
      restrictions: s.restrictions ?? '',
      specialNotes: s.specialNotes ?? ''
    });

    // EXW select binds option values with [value] (strings), so the model value
    // must be the string form to match an option; buildRequest coerces back with
    // Number().
    this.fg.controls.exwId.setValue(String(s.exwId));

    this.seedCategory(s.categoryId);
    this.seedAttributes(s.attributes);

    // Cost, Price and the DQ tiers are deliberately NOT seeded (2026-07-28). The
    // dividing line for a clone is: copy what describes the product, reset what
    // prices it. Commercial terms are the facet most likely to be wrong when
    // copied — and the target-company selector makes that sharper, since a clone
    // into another company carries a different cost basis and margin entirely, so
    // inherited numbers would be wrong by construction rather than merely stale.
    // Both are required at Create (Cost for every role, Price for operators), so
    // the form makes the user state them instead of letting stale money through
    // unnoticed.
  }

  /** Resolve the effective categoryId (leaf) back to the main + optional sub the
   *  two selects expect, walking the same one-level tree onMainCategoryChange uses.
   *  Unresolvable ids (e.g. a legacy category no longer in the lookup tree) leave
   *  the selects blank so the required validator makes the user pick. */
  private seedCategory(categoryId?: string | null): void {
    if (!categoryId) {
      return;
    }
    const topLevel = this.categories.find(c => c.categoryId === categoryId);
    if (topLevel) {
      this.fg.controls.categoryMainId.setValue(categoryId);
      this.subCategories = topLevel.children ?? [];
      this.fg.controls.categoryId.setValue(categoryId);
      return;
    }
    const parent = this.categories.find(c => c.children?.some(ch => ch.categoryId === categoryId));
    if (parent) {
      this.fg.controls.categoryMainId.setValue(parent.categoryId);
      this.subCategories = parent.children ?? [];
      this.fg.controls.categoryId.setValue(categoryId);
    }
  }

  /** Seed the three modelled attributes by matching the source attribute's
   *  attributeId to the resolved Condition/Warranty/Packaging lookup id; the
   *  control value is the value CODE (what the option binds). Attributes on the
   *  source outside these three are not modelled by the Create form and are left
   *  out. */
  private seedAttributes(attributes: ICreateItemAttribute[]): void {
    this.seedAttribute('condition', this.condition, attributes);
    this.seedAttribute('warranty', this.warranty, attributes);
    this.seedAttribute('packaging', this.packaging, attributes);
  }

  private seedAttribute(control: string, lookup: ILookupAttribute | undefined, attributes: ICreateItemAttribute[]): void {
    if (!lookup) {
      return;
    }
    const match = attributes.find(a => a.attributeId === lookup.id);
    if (match?.value) {
      this.fg.controls[control].setValue(match.value);
    }
  }

  /** Main category picked: expose its children (if any) and resolve categoryId
   *  to the main node when it is already a leaf. */
  onMainCategoryChange(): void {
    const mainId = this.fg.controls.categoryMainId.value;
    const main = this.categories.find(c => c.categoryId === mainId);
    this.subCategories = main && main.children ? main.children : [];
    this.fg.controls.categoryId.setValue(this.subCategories.length ? '' : mainId);
  }

  onSubCategoryChange(): void {
    // categoryId is bound directly to the sub select; nothing else to derive.
  }

  hasText(field: 'restrictions' | 'specialNotes'): boolean {
    const value = this.fg.controls[field].value;
    return !!value && !!value.trim();
  }

  openTextModal(field: 'restrictions' | 'specialNotes'): void {
    this.textModalField = field;
  }

  onTextModalSaved(text: string): void {
    if (this.textModalField) {
      this.fg.controls[this.textModalField].setValue(text);
    }
    this.textModalField = null;
  }

  onTextModalClosed(): void {
    this.textModalField = null;
  }

  close(): void {
    this.closed.emit();
  }

  submit(): void {
    this.submitted = true;
    this.serverError = null;
    if (this.fg.invalid) {
      // reveal every offending field at once (rings) and surface the banner
      this.fg.markAllAsTouched();
      return;
    }

    this.loader = true;
    this.itemCreateService.createItem(this.buildRequest())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.loader = false;
          // parent (items.component) closes the modal, reloads the list, and shows
          // the confirmation. The code comes from the form — the ack does carry it,
          // but the form value is exactly what was just submitted. The category
          // notice (HUBX-8429) is resolved to a sentence HERE, while the lookup tree
          // is still in hand: once the modal closes there is no category control to
          // sit beside, so the advisory rides the confirmation instead.
          this.created.emit({
            id: result.id,
            vendorItemCode: (this.fg.value.vendorItemCode ?? '').trim(),
            categoryNotice: this.categoryNoticeFrom(result.warnings) ?? undefined
          });
        },
        error: (err: HttpErrorResponse) => {
          this.loader = false;
          this.applyServerError(err);
        }
      });
  }

  /** Map the form to the API contract (ICreateItemRequest): optional members are
   *  OMITTED when empty (never null), attributes carry {attributeId, value:code},
   *  createApproved is only sent for ADMIN/VM, and SELLER-disabled controls
   *  (UnitPrice/XID/DQ/Status) drop out of fg.value on their own. */
  private buildRequest(): ICreateItemRequest {
    const v = this.fg.value as { [key: string]: any };

    const attributes: ICreateItemAttribute[] = [];
    if (this.condition && v.condition) { attributes.push({ attributeId: this.condition.id, value: v.condition }); }
    if (this.warranty && v.warranty) { attributes.push({ attributeId: this.warranty.id, value: v.warranty }); }
    if (this.packaging && v.packaging) { attributes.push({ attributeId: this.packaging.id, value: v.packaging }); }

    const request: ICreateItemRequest = {
      vendorItemCode: (v.vendorItemCode ?? '').trim(),
      mpn: (v.mpn ?? '').trim(),
      description: (v.description ?? '').trim(),
      manufacturerId: v.manufacturerId,
      categoryId: v.categoryId,
      availability: Number(v.availability),
      leadTimeDays: Number(v.leadTimeDays),
      exwId: Number(v.exwId),
      attributes
    };

    const xid = (v.hubxItemCode ?? '').trim();
    if (xid) { request.hubxItemCode = xid; }
    if (v.cost != null && v.cost !== '') { request.unitPurchasePrice = Number(v.cost); }
    if (v.unitPrice != null && v.unitPrice !== '') { request.unitPrice = Number(v.unitPrice); }
    if (v.moq != null && v.moq !== '') { request.minimumOrderQty = Number(v.moq); }
    if (v.mxq != null && v.mxq !== '') { request.maximumOrderQty = Number(v.mxq); }
    if (this.isPricingRole) { request.createApproved = v.status === 'Active'; }

    // Target company (ADR-0010): send the selection as-is. Whether it counts as a
    // real retarget is decided server-side, where the caller's vendor context is
    // authoritative — a target equal to that context is collapsed to "no retarget"
    // there (ItemsController), and only a DIFFERING value runs the target-vendor
    // policy. The client does NOT pre-null against its own notion of the ambient
    // vendor: that notion comes from sessionStorage and can lag the real context, so
    // omitting on a client-side match risks silently creating under a company other
    // than the one the selector shows. For a SELLER the control is disabled, so
    // `v.targetVendorId` is undefined here and nothing is sent.
    if (v.targetVendorId) {
      request.targetVendorId = v.targetVendorId;
    }

    const specialNotes = (v.specialNotes ?? '').trim();
    if (specialNotes) { request.specialNotes = specialNotes; }
    const restrictions = (v.restrictions ?? '').trim();
    if (restrictions) { request.restrictions = restrictions; }

    const discountQtys: ICreateItemDiscountQty[] = (v.discountQtys ?? [])
      .filter((d: any) => d && d.fromQty != null && d.fromQty !== '' && d.price != null && d.price !== '')
      .map((d: any) => ({ fromQty: Number(d.fromQty), price: Number(d.price) }));
    if (discountQtys.length) { request.discountQtys = discountQtys; }

    return request;
  }

  /** Route a ProblemDetails failure by its stable `code` (§8.1): ring the mapped
   *  control where there is one, and surface a client-owned message in the banner.
   *  The server `message`/`detail` is diagnostic only, so unknown/unmapped codes
   *  fall back to a generic line rather than echoing server text. */
  private applyServerError(err: HttpErrorResponse): void {
    const problem = (err?.error ?? {}) as IItemProblemDetails;
    const info = problem.code ? ItemCreateModalComponent.errorInfo[problem.code] : undefined;

    if (info?.control) {
      const control = this.fg.get(info.control);
      control?.setErrors({ server: true });
      control?.markAsTouched();
      // the category ring keys off the main select's touched state
      if (info.control === 'categoryId') {
        this.fg.get('categoryMainId')?.markAsTouched();
      }
    }

    this.serverError = info?.message ?? 'Could not create the item. Please try again.';
  }

  /** ProblemDetails code -> optional control to ring + the banner message. The
   *  copy is client-owned (design doc §8.1: the server message is diagnostic only). */
  private static readonly errorInfo: { [code: string]: { control?: string; message: string } } = {
    DUPLICATE_VENDOR_ITEM_CODE: { control: 'vendorItemCode', message: 'A vendor item with this Part Number already exists.' },
    // No CATEGORY_MISMATCH: the server stopped emitting it when category became a
    // per-vendor facet (ADR-0007 amended, HUBX-8428) — a category differing from the
    // shared item's is allowed, and is now the non-blocking nudge (categoryNudge)
    // plus the write-ack warning, never a failure.
    MANUFACTURER_NOT_FOUND: { control: 'manufacturerId', message: 'That manufacturer could not be found.' },
    MISSING_ITEM_KEY: { message: 'This vendor is not fully set up to create items (missing item key). Contact HUBX.' },
    RESTRICTION_ATTRIBUTE_UNAVAILABLE: { message: 'Restrictions are not available right now.' },
    CREATE_APPROVED_NOT_ALLOWED: { message: 'You are not allowed to create this item as approved.' },
    UNIT_PRICE_NOT_ALLOWED: { control: 'unitPrice', message: 'You are not allowed to set the sale price.' },
    DISCOUNT_QTYS_NOT_ALLOWED: { message: 'You are not allowed to set discount quantities.' },
    UNIT_PRICE_REQUIRED: { control: 'unitPrice', message: 'Price is required.' },
    UNIT_PURCHASE_PRICE_REQUIRED: { control: 'cost', message: 'Cost is required.' },
    UNIT_PRICE_BELOW_COST: { control: 'unitPrice', message: 'Price cannot be less than Cost.' },
    MOQ_EXCEEDS_MXQ: { control: 'mxq', message: 'MOQ cannot be greater than MXQ.' },
    DISCOUNT_QTY_BELOW_MINIMUM: { message: "Discount 'From Qty' must be greater than 1." },
    DISCOUNT_PRICE_EXCEEDS_UNIT_PRICE: { message: 'Discount price cannot exceed or equal the price.' },
    DISCOUNT_PRICE_BELOW_COST_MARGIN: { message: 'Discount price is below the allowed cost margin.' },
    REQUIRED_ATTRIBUTES_MISSING: { message: 'Condition, Warranty and Packaging are required.' },
    VENDOR_CONTEXT_UNRESOLVED: { message: 'Your vendor session could not be resolved. Reload and try again.' },
    // Target company (ADR-0010). Three distinct outcomes, three distinct messages:
    // not permitted, not a valid target, and "we could not tell" — collapsing the
    // last into the first would tell the user they lack access when the truth is a
    // dependency was down, and they'd stop retrying something that would work.
    TARGET_VENDOR_NOT_ALLOWED: { control: 'targetVendorId', message: 'You are not allowed to create items for that company.' },
    TARGET_VENDOR_NOT_ELIGIBLE: { control: 'targetVendorId', message: 'That company cannot receive new items.' },
    TARGET_VENDOR_CHECK_UNAVAILABLE: { message: 'Could not verify your access to that company right now. Try again in a moment.' },
    CONCURRENCY_CONFLICT: { message: 'The item was changed elsewhere. Reload and try again.' }
  };

  /** The dialog stops click propagation so nothing inside it reaches the document
   *  (modal isolation). That also starves any document-level click-outside listener,
   *  so the company picker is closed here instead: any click that lands outside the
   *  picker's own container (`.clone-target`) closes it. The toggle and the search box
   *  live inside that container and keep their own behaviour; an option click has
   *  already closed the panel by the time it bubbles here. */
  onDialogClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.companyPickerOpen && !(event.target as HTMLElement).closest('.clone-target')) {
      this.companyPickerOpen = false;
    }
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }
}
